import React, { useState, useEffect, useCallback } from "react";
import { 
  Database, Download, Upload, Trash2, Shield, ShieldOff, RefreshCw, 
  Clock, HardDrive, Cloud, Server, Calendar, Settings, FileText, 
  CheckCircle, XCircle, AlertCircle, Play, Pause, Plus, Eye,
  FolderArchive, Loader2, ChevronRight, ExternalLink, Zap
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import { useToast } from "../../hooks/use-toast";
import SelectiveRestoreModal from "../../components/SelectiveRestoreModal";
import { Checkbox } from "../../components/ui/checkbox";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Separator } from "../../components/ui/separator";
import { RequirePermission } from "../../components/permissions/RequirePermission";
import { usePermissions } from "../../contexts/PermissionsContext";
import { PERMS } from "../../utils/permissions";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper pour formater les tailles
const formatSize = (bytes) => {
  if (!bytes) return "0 o";
  const sizes = ["o", "Ko", "Mo", "Go", "To"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

// Helper pour formater les dates
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

// Icône de destination
const DestinationIcon = ({ type }) => {
  switch (type) {
    case "local": return <HardDrive className="w-4 h-4" />;
    case "s3": return <Cloud className="w-4 h-4" />;
    case "linode": return <Cloud className="w-4 h-4" />;
    case "ftp":
    case "sftp": return <Server className="w-4 h-4" />;
    case "scp": return <Server className="w-4 h-4" />;
    default: return <Database className="w-4 h-4" />;
  }
};

// Badge de statut
const StatusBadge = ({ status }) => {
  const config = {
    running: { color: "bg-blue-100 text-blue-700", icon: Loader2, label: "En cours" },
    completed: { color: "bg-green-100 text-green-700", icon: CheckCircle, label: "Terminé" },
    verified: { color: "bg-emerald-100 text-emerald-700", icon: Shield, label: "Vérifié" },
    failed: { color: "bg-red-100 text-red-700", icon: XCircle, label: "Échoué" },
    pending: { color: "bg-yellow-100 text-yellow-700", icon: Clock, label: "En attente" }
  };
  
  const { color, icon: Icon, label } = config[status] || config.pending;
  
  return (
    <Badge className={`${color} flex items-center gap-1`}>
      <Icon className={`w-3 h-3 ${status === "running" ? "animate-spin" : ""}`} />
      {label}
    </Badge>
  );
};

// Composant principal
const BackupManagement = () => {
  const { toast } = useToast();
  const { hasPermission, isAdmin } = usePermissions();
  
  const canCreate = isAdmin || hasPermission(PERMS.BACKUPS_CREATE);
  const canDelete = isAdmin || hasPermission(PERMS.BACKUPS_DELETE);
  const canRestore = isAdmin || hasPermission(PERMS.BACKUPS_RESTORE);
  const canDownload = isAdmin || hasPermission(PERMS.BACKUPS_DOWNLOAD);
  
  const [activeTab, setActiveTab] = useState("backups");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Données
  const [backups, setBackups] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [retentionConfig, setRetentionConfig] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  
  // Modals
  const [showCreateBackupModal, setShowCreateBackupModal] = useState(false);
  const [showDestinationModal, setShowDestinationModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showSelectiveRestore, setShowSelectiveRestore] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  
  // États de formulaire
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [editingDestination, setEditingDestination] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [confirmKeyword, setConfirmKeyword] = useState("");
  
  // Polling pour les backups en cours
  const [pollingActive, setPollingActive] = useState(false);

  // Fetch des données
  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/backups/stats`, {});
      if (res.ok) {
        setStats(await res.json());
      }
    } catch (error) {
      console.error("Erreur stats:", error);
    }
  }, []);

  const fetchBackups = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/backups?limit=50`, {});
      if (res.ok) {
        const data = await res.json();
        setBackups(data.backups || []);
        
        // Activer le polling si un backup est en cours
        const hasRunning = data.backups?.some(b => b.status === "running");
        setPollingActive(hasRunning);
      }
    } catch (error) {
      console.error("Erreur backups:", error);
    }
  }, []);

  const fetchDestinations = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/backups/destinations`, {});
      if (res.ok) {
        const data = await res.json();
        setDestinations(data.destinations || []);
      }
    } catch (error) {
      console.error("Erreur destinations:", error);
    }
  }, []);

  const fetchSchedules = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/backups/schedules`, {});
      if (res.ok) {
        const data = await res.json();
        setSchedules(data.schedules || []);
      }
    } catch (error) {
      console.error("Erreur schedules:", error);
    }
  }, []);

  const fetchRetention = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/backups/retention`, {});
      if (res.ok) {
        setRetentionConfig(await res.json());
      }
    } catch (error) {
      console.error("Erreur retention:", error);
    }
  }, []);

  const fetchAuditLogs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/backups/audit-logs?limit=100`, {});
      if (res.ok) {
        const data = await res.json();
        setAuditLogs(data.logs || []);
      }
    } catch (error) {
      console.error("Erreur audit logs:", error);
    }
  }, []);

  // Charger les données initiales
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchStats(),
        fetchBackups(),
        fetchDestinations(),
        fetchSchedules(),
        fetchRetention()
      ]);
      setLoading(false);
    };
    loadAll();
  }, [fetchStats, fetchBackups, fetchDestinations, fetchSchedules, fetchRetention]);

  // Polling pour les backups en cours
  useEffect(() => {
    let interval;
    if (pollingActive) {
      interval = setInterval(() => {
        fetchBackups();
        fetchStats();
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [pollingActive, fetchBackups, fetchStats]);

  // Charger les logs quand on change d'onglet
  useEffect(() => {
    if (activeTab === "audit") {
      fetchAuditLogs();
    }
  }, [activeTab, fetchAuditLogs]);

  // Actions
  const getToken = () => 'cookie-auth';
  
  const handleQuickBackup = async () => {
    try {
      const res = await fetch(`${API_URL}/api/backups/quick`, {
        method: "POST"});
      const data = await res.json();
      if (res.ok) {
        toast({ title: "Sauvegarde rapide démarrée", description: "La sauvegarde complète est en cours..." });
        fetchBackups();
        fetchStats();
      } else {
        toast({ title: "Erreur", description: data.detail || "Erreur lors du démarrage", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Erreur réseau", variant: "destructive" });
    }
  };

  const handleImportBackup = async (file, comment) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("comment", comment || `Import de ${file.name}`);
    try {
      const res = await fetch(`${API_URL}/api/backups/import`, {
        method: "POST",
        body: formData
      });
      const data = await res.json();
      if (res.ok) {
        if (data.already_exists) {
          toast({ title: "Déjà présent", description: "Ce backup existe déjà dans le système (checksum identique)" });
        } else {
          toast({
            title: "Import réussi",
            description: `${data.size_mb} Mo importés — ${data.collection_count} collections`
          });
          fetchBackups();
          fetchStats();
        }
        setShowImportModal(false);
      } else {
        toast({ title: "Erreur d'import", description: data.detail || "Fichier invalide", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Erreur réseau", variant: "destructive" });
    }
  };

  const handleCreateBackup = async (formData) => {
    try {
      const res = await fetch(`${API_URL}/api/backups`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast({ title: "Succès", description: "Backup démarré avec succès" });
        setShowCreateBackupModal(false);
        fetchBackups();
        fetchStats();
      } else {
        toast({ title: "Erreur", description: data.detail || "Erreur lors de la création", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur réseau", variant: "destructive" });
    }
  };

  const handleDeleteBackup = async () => {
    if (!selectedBackup) return;
    
    try {
      const res = await fetch(`${API_URL}/api/backups/${selectedBackup.id}`, {
        method: "DELETE"});
      
      if (res.ok) {
        toast({ title: "Succès", description: "Backup supprimé" });
        setShowDeleteDialog(false);
        setSelectedBackup(null);
        fetchBackups();
        fetchStats();
      } else {
        const data = await res.json();
        toast({ title: "Erreur", description: data.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur réseau", variant: "destructive" });
    }
  };

  const handleToggleProtection = async (backup) => {
    try {
      const res = await fetch(`${API_URL}/api/backups/${backup.id}/protect`, {
        method: "PUT"});
      
      if (res.ok) {
        const data = await res.json();
        toast({ title: "Succès", description: data.message });
        fetchBackups();
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur réseau", variant: "destructive" });
    }
  };

  const handleVerifyBackup = async (backup) => {
    try {
      const res = await fetch(`${API_URL}/api/backups/${backup.id}/verify`, {
        method: "POST"});
      
      if (res.ok) {
        const data = await res.json();
        if (data.verification?.overall === "success") {
          toast({ title: "Succès", description: "Backup vérifié avec succès" });
        } else {
          toast({ title: "Attention", description: "Problèmes détectés lors de la vérification", variant: "destructive" });
        }
        fetchBackups();
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur réseau", variant: "destructive" });
    }
  };

  const handleDownloadBackup = async (backup) => {
    try {
      // Obtenir le lien de téléchargement temporaire
      const res = await fetch(`${API_URL}/api/backups/${backup.id}/download-link`, {
        method: "POST"});
      
      if (res.ok) {
        const data = await res.json();
        // Ouvrir le lien de téléchargement
        window.open(`${API_URL}${data.download_url}`, "_blank");
        toast({ title: "Succès", description: "Téléchargement démarré" });
      } else {
        const data = await res.json();
        toast({ title: "Erreur", description: data.detail || "Impossible de télécharger", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur réseau", variant: "destructive" });
    }
  };

  const handleRestore = async (formData) => {
    try {
      const res = await fetch(`${API_URL}/api/backups/restore`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast({ title: "Succès", description: "Restauration démarrée" });
        setShowRestoreModal(false);
        setSelectedBackup(null);
      } else {
        const data = await res.json();
        toast({ title: "Erreur", description: data.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur réseau", variant: "destructive" });
    }
  };

  const handleSaveRetention = async () => {
    try {
      const payload = {
        cleanup_enabled: retentionConfig?.cleanup_enabled ?? true,
        cleanup_frequency: retentionConfig?.cleanup_frequency || "daily",
        cleanup_hour: retentionConfig?.cleanup_hour ?? 4,
        keep_last_n: Math.max(1, parseInt(retentionConfig?.keep_last_n ?? 7, 10) || 7),
      };
      const res = await fetch(`${API_URL}/api/backups/retention`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast({ title: "Configuration enregistrée", description: `Conservation : ${payload.keep_last_n} backups les plus récents (hors protégés).` });
        fetchRetention();
      } else {
        const err = await res.json().catch(() => ({}));
        toast({ title: "Erreur", description: err.detail || "Échec de l'enregistrement.", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur réseau", variant: "destructive" });
    }
  };

  const handleRunCleanup = async () => {
    try {
      const res = await fetch(`${API_URL}/api/backups/retention/cleanup`, {
        method: "POST"});
      
      if (res.ok) {
        const data = await res.json();
        toast({ 
          title: "Nettoyage terminé", 
          description: `${data.deleted_count} backup(s) supprimé(s), ${data.freed_space_mb} Mo libérés` 
        });
        fetchBackups();
        fetchStats();
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur réseau", variant: "destructive" });
    }
  };

  // Destinations handlers
  const handleSaveDestination = async (formData) => {
    try {
      const method = editingDestination?.id ? "PUT" : "POST";
      const url = editingDestination?.id 
        ? `${API_URL}/api/backups/destinations/${editingDestination.id}`
        : `${API_URL}/api/backups/destinations`;
      
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast({ title: "Succès", description: editingDestination?.id ? "Destination modifiée" : "Destination créée" });
        setShowDestinationModal(false);
        setEditingDestination(null);
        fetchDestinations();
      } else {
        const data = await res.json();
        toast({ title: "Erreur", description: data.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur réseau", variant: "destructive" });
    }
  };

  const handleTestDestination = async (destId) => {
    try {
      const res = await fetch(`${API_URL}/api/backups/destinations/${destId}/test`, {
        method: "POST"});
      
      const data = await res.json();
      
      if (data.success) {
        toast({ title: "Connexion réussie", description: data.message });
      } else {
        toast({ title: "Échec de connexion", description: data.message, variant: "destructive" });
      }
      
      fetchDestinations();
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur réseau", variant: "destructive" });
    }
  };

  const handleDeleteDestination = async (destId) => {
    try {
      const res = await fetch(`${API_URL}/api/backups/destinations/${destId}`, {
        method: "DELETE"});
      
      if (res.ok) {
        toast({ title: "Succès", description: "Destination supprimée" });
        fetchDestinations();
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur réseau", variant: "destructive" });
    }
  };

  // Schedule handlers
  const handleSaveSchedule = async (formData) => {
    try {
      const method = editingSchedule?.id ? "PUT" : "POST";
      const url = editingSchedule?.id 
        ? `${API_URL}/api/backups/schedules/${editingSchedule.id}`
        : `${API_URL}/api/backups/schedules`;
      
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast({ title: "Succès", description: editingSchedule?.id ? "Planification modifiée" : "Planification créée" });
        setShowScheduleModal(false);
        setEditingSchedule(null);
        fetchSchedules();
      } else {
        const data = await res.json();
        toast({ title: "Erreur", description: data.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur réseau", variant: "destructive" });
    }
  };

  const handleRunScheduleNow = async (scheduleId) => {
    try {
      const res = await fetch(`${API_URL}/api/backups/schedules/${scheduleId}/run`, {
        method: "POST"});
      
      if (res.ok) {
        toast({ title: "Succès", description: "Backup démarré" });
        fetchBackups();
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur réseau", variant: "destructive" });
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      const res = await fetch(`${API_URL}/api/backups/schedules/${scheduleId}`, {
        method: "DELETE"});
      
      if (res.ok) {
        toast({ title: "Succès", description: "Planification supprimée" });
        fetchSchedules();
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur réseau", variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total backups</p>
                <p className="text-2xl font-bold">{stats?.total_backups || 0}</p>
              </div>
              <FolderArchive className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Espace local utilisé</p>
                <p className="text-2xl font-bold">{stats?.local_space_used_gb || 0} Go</p>
              </div>
              <HardDrive className="w-8 h-8 text-emerald-500" />
            </div>
            <Progress 
              value={stats?.local_space_percent || 0} 
              className="mt-2 h-2" 
            />
            <p className="text-xs text-gray-400 mt-1">
              {stats?.local_space_available_gb || 0} Go disponibles
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Backups protégés</p>
                <p className="text-2xl font-bold">{stats?.protected_backups || 0}</p>
              </div>
              <Shield className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Planifications actives</p>
                <p className="text-2xl font-bold">{stats?.active_schedules || 0}</p>
              </div>
              <Calendar className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex w-full overflow-x-auto h-auto p-1 bg-gray-100 rounded-xl">
          <TabsTrigger value="backups" className="flex items-center gap-2 py-2">
            <FolderArchive className="w-4 h-4" />
            <span className="hidden sm:inline">Backups</span>
          </TabsTrigger>
          <TabsTrigger value="destinations" className="flex items-center gap-2 py-2">
            <Server className="w-4 h-4" />
            <span className="hidden sm:inline">Destinations</span>
          </TabsTrigger>
          <TabsTrigger value="schedules" className="flex items-center gap-2 py-2">
            <Calendar className="w-4 h-4" />
            <span className="hidden sm:inline">Planifications</span>
          </TabsTrigger>
          <TabsTrigger value="retention" className="flex items-center gap-2 py-2">
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Rétention</span>
          </TabsTrigger>
          <TabsTrigger value="audit" className="flex items-center gap-2 py-2">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Journal</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab: Backups */}
        <TabsContent value="backups" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Sauvegardes</CardTitle>
                <CardDescription>Liste des sauvegardes disponibles</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { fetchBackups(); fetchStats(); }}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Actualiser
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-blue-500 text-blue-600 hover:bg-blue-50"
                  onClick={() => setShowImportModal(true)}
                  data-testid="import-backup-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Importer
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                  onClick={handleQuickBackup}
                  data-testid="quick-backup-btn"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Sauvegarde rapide
                </Button>
                <Button size="sm" onClick={() => setShowCreateBackupModal(true)} disabled={!canCreate} title={!canCreate ? 'Permission requise : backups.create' : ''} data-testid="create-backup-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un backup
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {backups.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FolderArchive className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun backup disponible</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowCreateBackupModal(true)}
                    disabled={!canCreate}
                    title={!canCreate ? 'Permission requise : backups.create' : ''}
                    data-testid="create-first-backup-btn"
                  >
                    Créer votre premier backup
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {backups.map((backup) => (
                    <div 
                      key={backup.id} 
                      className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <FolderArchive className="w-5 h-5 text-gray-600" />
                            <span className="font-medium">{backup.name}</span>
                            <StatusBadge status={backup.status} />
                            {backup.protected && (
                              <Badge variant="outline" className="text-amber-600 border-amber-300">
                                <Shield className="w-3 h-3 mr-1" />
                                Protégé
                              </Badge>
                            )}
                          </div>
                          
                          <div className="text-sm text-gray-500 space-y-1">
                            <p>
                              Créé le {formatDate(backup.created_at)} par {backup.created_by}
                            </p>
                            <p>
                              Taille: {formatSize(backup.size_bytes || 0)}
                            </p>
                            <p>
                              Type: {backup.backup_type === "full" ? "Complet" : 
                                    backup.backup_type === "data_only" ? "Données seulement" : "Fichiers seulement"}
                              {backup.compression && backup.compression !== "none" && (
                                <span> • Compression: {backup.compression.toUpperCase()}</span>
                              )}
                            </p>
                            {backup.expires_at && !backup.protected && (
                              <p className="text-amber-600">
                                Expire le {formatDate(backup.expires_at)}
                              </p>
                            )}
                            {backup.comment && (
                              <p className="italic">"{backup.comment}"</p>
                            )}
                          </div>
                          
                          {backup.status === "running" && (
                            <div className="mt-3">
                              <div className="flex items-center justify-between text-sm mb-1">
                                <span>{backup.current_step}</span>
                                <span>{backup.progress}%</span>
                              </div>
                              <Progress value={backup.progress} className="h-2" />
                            </div>
                          )}
                        </div>
                        
                        {backup.status !== "running" && (
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDownloadBackup(backup)}
                              title={canDownload ? "Télécharger" : "Permission requise : backups.download"}
                              disabled={!canDownload}
                              data-testid={`download-backup-${backup.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleVerifyBackup(backup)}
                              title="Vérifier l'intégrité"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setSelectedBackup(backup);
                                setShowRestoreModal(true);
                              }}
                              title={canRestore ? "Restaurer" : "Permission requise : backups.restore"}
                              disabled={!canRestore}
                              data-testid={`restore-backup-${backup.id}`}
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setSelectedBackup(backup);
                                setShowSelectiveRestore(true);
                              }}
                              title={canRestore ? "Restauration selective (delta)" : "Permission requise : backups.restore"}
                              disabled={!canRestore}
                              data-testid={`selective-restore-${backup.id}`}
                            >
                              <Settings className="w-4 h-4 text-amber-600" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleToggleProtection(backup)}
                              title={canDelete ? (backup.protected ? "Déprotéger" : "Protéger") : "Permission requise : backups.create"}
                              disabled={!canCreate}
                            >
                              {backup.protected ? (
                                <ShieldOff className="w-4 h-4" />
                              ) : (
                                <Shield className="w-4 h-4" />
                              )}
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setSelectedBackup(backup);
                                setShowDeleteDialog(true);
                              }}
                              title={canDelete ? "Supprimer" : "Permission requise : backups.delete"}
                              disabled={backup.protected || !canDelete}
                              data-testid={`delete-backup-${backup.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Destinations */}
        <TabsContent value="destinations" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Destinations de stockage</CardTitle>
                <CardDescription>Configurez où stocker vos sauvegardes</CardDescription>
              </div>
              <Button size="sm" onClick={() => {
                setEditingDestination(null);
                setShowDestinationModal(true);
              }}>
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle destination
              </Button>
            </CardHeader>
            <CardContent>
              {destinations.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Server className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune destination configurée</p>
                  <p className="text-sm mt-2">Ajoutez une destination locale ou distante</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {destinations.map((dest) => (
                    <div 
                      key={dest.id} 
                      className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-gray-100 rounded-lg">
                            <DestinationIcon type={dest.type} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{dest.name}</span>
                              <Badge variant={dest.enabled ? "default" : "secondary"}>
                                {dest.enabled ? "Activé" : "Désactivé"}
                              </Badge>
                              {dest.last_test_status && (
                                <Badge variant={dest.last_test_status === "success" ? "outline" : "destructive"}>
                                  {dest.last_test_status === "success" ? "Connecté" : "Erreur"}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              Type: {dest.type.toUpperCase()}
                              {dest.config?.path && ` • ${dest.config.path}`}
                              {dest.config?.host && ` • ${dest.config.host}`}
                              {dest.config?.bucket && ` • Bucket: ${dest.config.bucket}`}
                            </p>
                            {dest.last_test && (
                              <p className="text-xs text-gray-400 mt-1">
                                Dernier test: {formatDate(dest.last_test)}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleTestDestination(dest.id)}
                          >
                            Tester
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setEditingDestination(dest);
                              setShowDestinationModal(true);
                            }}
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteDestination(dest.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Schedules */}
        <TabsContent value="schedules" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Planifications automatiques</CardTitle>
                <CardDescription>Configurez les sauvegardes automatiques</CardDescription>
              </div>
              <Button size="sm" onClick={() => {
                setEditingSchedule(null);
                setShowScheduleModal(true);
              }}>
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle planification
              </Button>
            </CardHeader>
            <CardContent>
              {schedules.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune planification configurée</p>
                  <p className="text-sm mt-2">Créez des backups automatiques</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {schedules.map((schedule) => (
                    <div 
                      key={schedule.id} 
                      className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-gray-600" />
                            <span className="font-medium">{schedule.name}</span>
                            <Badge variant={schedule.enabled ? "default" : "secondary"}>
                              {schedule.enabled ? "Activé" : "Désactivé"}
                            </Badge>
                          </div>
                          <div className="text-sm text-gray-500 mt-2 space-y-1">
                            <p>
                              Fréquence: {schedule.frequency === "daily" ? "Quotidien" : 
                                         schedule.frequency === "weekly" ? "Hebdomadaire" : "Mensuel"}
                              {" à "}{String(schedule.hour).padStart(2, "0")}:{String(schedule.minute).padStart(2, "0")}
                            </p>
                            <p>
                              Type: {schedule.backup_type === "full" ? "Complet" : 
                                    schedule.backup_type === "data_only" ? "Données" : "Fichiers"}
                              • Rétention: {schedule.retention_days} jours
                            </p>
                            {schedule.last_run && (
                              <p>
                                Dernier run: {formatDate(schedule.last_run)}
                                {schedule.last_run_status && (
                                  <span className={schedule.last_run_status === "success" ? "text-green-600" : "text-red-600"}>
                                    {" "} ({schedule.last_run_status === "success" ? "Succès" : "Échec"})
                                  </span>
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleRunScheduleNow(schedule.id)}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Exécuter
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setEditingSchedule(schedule);
                              setShowScheduleModal(true);
                            }}
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteSchedule(schedule.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Retention */}
        <TabsContent value="retention" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Politique de rétention</CardTitle>
              <CardDescription>Gérez la suppression automatique des anciens backups</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Nettoyage automatique</p>
                  <p className="text-sm text-gray-500">Supprimé automatiquement les backups expirés</p>
                </div>
                <Switch 
                  checked={retentionConfig?.cleanup_enabled ?? true}
                  onCheckedChange={(checked) => setRetentionConfig({...retentionConfig, cleanup_enabled: checked})}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fréquence de nettoyage</Label>
                  <Select 
                    value={retentionConfig?.cleanup_frequency || "daily"}
                    onValueChange={(v) => setRetentionConfig({...retentionConfig, cleanup_frequency: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Quotidien</SelectItem>
                      <SelectItem value="weekly">Hebdomadaire</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Heure d'exécution</Label>
                  <Select 
                    value={String(retentionConfig?.cleanup_hour || 4)}
                    onValueChange={(v) => setRetentionConfig({...retentionConfig, cleanup_hour: parseInt(v)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({length: 24}, (_, i) => (
                        <SelectItem key={i} value={String(i)}>
                          {String(i).padStart(2, "0")}:00
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 border-2 border-[#1db584]/30 bg-[#1db584]/5 rounded-lg">
                  <Label htmlFor="keep-last-n" className="font-medium">Conserver les N backups les plus récents</Label>
                  <p className="text-xs text-gray-500 mb-2">
                    Au-delà, les anciens backups <strong>non protégés</strong> sont purgés automatiquement après chaque sauvegarde. Les backups protégés ne sont jamais supprimés.
                  </p>
                  <Input
                    id="keep-last-n"
                    type="number"
                    min={1}
                    max={365}
                    className="w-32"
                    data-testid="retention-keep-last-n-input"
                    value={retentionConfig?.keep_last_n ?? 7}
                    onChange={(e) => setRetentionConfig({ ...retentionConfig, keep_last_n: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSaveRetention} data-testid="retention-save-btn" className="bg-[#1db584] hover:bg-[#17a074]">
                  Enregistrer la configuration
                </Button>
              </div>

              <Separator />
              
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">Exécuter le nettoyage maintenant</p>
                  <p className="text-sm text-gray-500">Supprimé immédiatement les backups expirés et non protégés</p>
                </div>
                <Button variant="outline" onClick={handleRunCleanup}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Nettoyer maintenant
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Audit */}
        <TabsContent value="audit" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Journal d'audit</CardTitle>
              <CardDescription>Historique des actions sur les sauvegardes</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {auditLogs.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Aucune action enregistrée</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {auditLogs.map((log) => (
                      <div key={log.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className={`p-2 rounded-full ${
                          log.action.includes("COMPLETED") || log.action.includes("CREATED") ? "bg-green-100" :
                          log.action.includes("FAILED") ? "bg-red-100" :
                          log.action.includes("DELETED") ? "bg-amber-100" : "bg-blue-100"
                        }`}>
                          {log.action.includes("COMPLETED") ? <CheckCircle className="w-4 h-4 text-green-600" /> :
                           log.action.includes("FAILED") ? <XCircle className="w-4 h-4 text-red-600" /> :
                           log.action.includes("DELETED") ? <Trash2 className="w-4 h-4 text-amber-600" /> :
                           <Clock className="w-4 h-4 text-blue-600" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm">{log.action.replace(/_/g, " ")}</span>
                            <span className="text-xs text-gray-400">{formatDate(log.timestamp)}</span>
                          </div>
                          <p className="text-sm text-gray-500">
                            Par {log.user_email}
                            {log.ip_address && <span> depuis {log.ip_address}</span>}
                          </p>
                          {log.details && Object.keys(log.details).length > 0 && (
                            <p className="text-xs text-gray-400 mt-1">
                              {JSON.stringify(log.details)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal: Create Backup */}
      <CreateBackupModal 
        open={showCreateBackupModal}
        onClose={() => setShowCreateBackupModal(false)}
        onSubmit={handleCreateBackup}
        destinations={destinations}
      />

      {/* Modal: Destination */}
      <DestinationModal 
        open={showDestinationModal}
        onClose={() => {
          setShowDestinationModal(false);
          setEditingDestination(null);
        }}
        onSubmit={handleSaveDestination}
        destination={editingDestination}
      />

      {/* Modal: Schedule */}
      <ScheduleModal 
        open={showScheduleModal}
        onClose={() => {
          setShowScheduleModal(false);
          setEditingSchedule(null);
        }}
        onSubmit={handleSaveSchedule}
        schedule={editingSchedule}
        destinations={destinations}
      />

      {/* Modal: Restore */}
      <RestoreModal 
        open={showRestoreModal}
        onClose={() => {
          setShowRestoreModal(false);
          setSelectedBackup(null);
          setConfirmKeyword("");
        }}
        onSubmit={handleRestore}
        backup={selectedBackup}
        confirmKeyword={confirmKeyword}
        setConfirmKeyword={setConfirmKeyword}
      />

      {/* Dialog: Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce backup ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le backup "{selectedBackup?.name}" sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedBackup(null)}>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteBackup}
              className="bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal Import Backup */}
      <ImportBackupModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportBackup}
      />

      {/* Selective Restore Modal */}
      {showSelectiveRestore && selectedBackup && (
        <SelectiveRestoreModal
          backup={selectedBackup}
          onClose={() => { setShowSelectiveRestore(false); setSelectedBackup(null); }}
          onComplete={() => fetchBackups()}
        />
      )}
    </div>
  );
};

// Modal: Create Backup
const CreateBackupModal = ({ open, onClose, onSubmit, destinations }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    backup_type: "full",
    destinations: [],
    compression: "gzip",
    compression_level: 6,
    retention_days: 90,
    protected: false,
    comment: ""
  });

  const handleSubmit = () => {
    if (formData.destinations.length === 0) return;
    onSubmit(formData);
    setStep(1);
    setFormData({
      backup_type: "full",
      destinations: [],
      compression: "gzip",
      compression_level: 6,
      retention_days: 90,
      protected: false,
      comment: ""
    });
  };

  const toggleDestination = (destId) => {
    setFormData(prev => ({
      ...prev,
      destinations: prev.destinations.includes(destId)
        ? prev.destinations.filter(d => d !== destId)
        : [...prev.destinations, destId]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Créer une sauvegarde</DialogTitle>
          <DialogDescription>Étape {step}/3</DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-4">
            <p className="font-medium">Que souhaitez-vous sauvegarder ?</p>
            
            <div className="space-y-3">
              {[
                { value: "full", label: "Sauvegarde complète", desc: "Base de données + fichiers" },
                { value: "data_only", label: "Données seulement", desc: "Base de données uniquement" },
                { value: "files_only", label: "Fichiers seulement", desc: "Documents et pièces jointes" }
              ].map(opt => (
                <div 
                  key={opt.value}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    formData.backup_type === opt.value ? "border-blue-500 bg-blue-50" : "hover:bg-gray-50"
                  }`}
                  onClick={() => setFormData({...formData, backup_type: opt.value})}
                >
                  <p className="font-medium">{opt.label}</p>
                  <p className="text-sm text-gray-500">{opt.desc}</p>
                </div>
              ))}
            </div>

            <div>
              <Label>Commentaire (optionnel)</Label>
              <Textarea 
                placeholder="Ex: Backup avant mise à jour v2.5"
                value={formData.comment}
                onChange={(e) => setFormData({...formData, comment: e.target.value})}
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 py-4">
            <p className="font-medium">Où stocker cette sauvegarde ?</p>
            
            {destinations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Server className="w-8 h-8 mx-auto mb-2" />
                <p>Aucune destination configurée</p>
                <p className="text-sm">Ajoutez d'abord une destination dans l'onglet Destinations</p>
              </div>
            ) : (
              <div className="space-y-2">
                {destinations.filter(d => d.enabled).map(dest => (
                  <div 
                    key={dest.id}
                    className={`p-3 border rounded-lg cursor-pointer flex items-center gap-3 ${
                      formData.destinations.includes(dest.id) ? "border-blue-500 bg-blue-50" : "hover:bg-gray-50"
                    }`}
                    onClick={() => toggleDestination(dest.id)}
                  >
                    <Checkbox checked={formData.destinations.includes(dest.id)} />
                    <DestinationIcon type={dest.type} />
                    <div>
                      <p className="font-medium">{dest.name}</p>
                      <p className="text-sm text-gray-500">{dest.type.toUpperCase()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Compression</Label>
                <Select 
                  value={formData.compression}
                  onValueChange={(v) => setFormData({...formData, compression: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gzip">GZIP (.tar.gz)</SelectItem>
                    <SelectItem value="zip">ZIP (.zip)</SelectItem>
                    <SelectItem value="none">Aucune</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Niveau ({formData.compression_level})</Label>
                <Input 
                  type="range"
                  min="1"
                  max="9"
                  value={formData.compression_level}
                  onChange={(e) => setFormData({...formData, compression_level: parseInt(e.target.value)})}
                />
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 py-4">
            <p className="font-medium">Conservation et récapitulatif</p>
            
            <div>
              <Label>Durée de conservation</Label>
              <Select 
                value={String(formData.retention_days)}
                onValueChange={(v) => setFormData({...formData, retention_days: v === "0" ? null : parseInt(v)})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 jours</SelectItem>
                  <SelectItem value="90">90 jours</SelectItem>
                  <SelectItem value="365">1 an</SelectItem>
                  <SelectItem value="0">Illimité</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox 
                checked={formData.protected}
                onCheckedChange={(v) => setFormData({...formData, protected: v})}
              />
              <Label className="cursor-pointer">Protéger ce backup (ne sera jamais supprimé automatiquement)</Label>
            </div>

            <Separator />

            <div className="p-4 bg-gray-50 rounded-lg space-y-2">
              <p className="font-medium">Récapitulatif</p>
              <p className="text-sm">
                Type: {formData.backup_type === "full" ? "Complet" : 
                      formData.backup_type === "data_only" ? "Données" : "Fichiers"}
              </p>
              <p className="text-sm">
                Destinations: {formData.destinations.length} sélectionnée(s)
              </p>
              <p className="text-sm">
                Compression: {formData.compression.toUpperCase()}
              </p>
              <p className="text-sm">
                Rétention: {formData.retention_days ? `${formData.retention_days} jours` : "Illimitée"}
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {step > 1 && (
            <Button variant="outline" onClick={() => setStep(s => s - 1)}>
              Retour
            </Button>
          )}
          {step < 3 ? (
            <Button 
              onClick={() => setStep(s => s + 1)}
              disabled={step === 2 && formData.destinations.length === 0}
            >
              Suivant
            </Button>
          ) : (
            <Button onClick={handleSubmit}>
              Démarrer la sauvegarde
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Modal: Destination
const DestinationModal = ({ open, onClose, onSubmit, destination }) => {
  const [formData, setFormData] = useState({
    name: "",
    type: "local",
    enabled: true,
    config: {}
  });

  useEffect(() => {
    if (destination) {
      setFormData({
        name: destination.name || "",
        type: destination.type || "local",
        enabled: destination.enabled ?? true,
        config: destination.config || {}
      });
    } else {
      setFormData({
        name: "",
        type: "local",
        enabled: true,
        config: { path: "/app/storage/backups" }
      });
    }
  }, [destination]);

  const handleSubmit = () => {
    if (!formData.name) return;
    onSubmit(formData);
  };

  const updateConfig = (key, value) => {
    setFormData(prev => ({
      ...prev,
      config: { ...prev.config, [key]: value }
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{destination ? "Modifier la destination" : "Nouvelle destination"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label>Nom de la destination *</Label>
            <Input 
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Ex: Backup Server Principal"
            />
          </div>

          <div>
            <Label>Type</Label>
            <Select 
              value={formData.type}
              onValueChange={(v) => setFormData({...formData, type: v, config: {}})}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="local">Stockage local</SelectItem>
                <SelectItem value="ftp">FTP</SelectItem>
                <SelectItem value="sftp">SFTP</SelectItem>
                <SelectItem value="scp">SCP / SSH</SelectItem>
                <SelectItem value="s3">AWS S3</SelectItem>
                <SelectItem value="linode">Linode Object Storage</SelectItem>
                <SelectItem value="s3_compatible">S3 Compatible (Wasabi, DO, etc.)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.type === "local" && (
            <div>
              <Label>Chemin du répertoire</Label>
              <Input 
                value={formData.config.path || ""}
                onChange={(e) => updateConfig("path", e.target.value)}
                placeholder="/app/storage/backups"
              />
            </div>
          )}

          {(formData.type === "ftp" || formData.type === "sftp") && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Hôte *</Label>
                  <Input 
                    value={formData.config.host || ""}
                    onChange={(e) => updateConfig("host", e.target.value)}
                    placeholder="ftp.example.com"
                  />
                </div>
                <div>
                  <Label>Port</Label>
                  <Input 
                    type="number"
                    value={formData.config.port || (formData.type === "sftp" ? 22 : 21)}
                    onChange={(e) => updateConfig("port", parseInt(e.target.value))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Utilisateur *</Label>
                  <Input 
                    value={formData.config.username || ""}
                    onChange={(e) => updateConfig("username", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Mot de passe *</Label>
                  <Input 
                    type="password"
                    value={formData.config.password || ""}
                    onChange={(e) => updateConfig("password", e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label>Répertoire distant</Label>
                <Input 
                  value={formData.config.remote_path || ""}
                  onChange={(e) => updateConfig("remote_path", e.target.value)}
                  placeholder="/backups/"
                />
              </div>
            </>
          )}

          {formData.type === "s3" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Access Key ID *</Label>
                  <Input 
                    value={formData.config.access_key || ""}
                    onChange={(e) => updateConfig("access_key", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Secret Access Key *</Label>
                  <Input 
                    type="password"
                    value={formData.config.secret_key || ""}
                    onChange={(e) => updateConfig("secret_key", e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Région *</Label>
                  <Input 
                    value={formData.config.region || ""}
                    onChange={(e) => updateConfig("region", e.target.value)}
                    placeholder="eu-west-3"
                  />
                </div>
                <div>
                  <Label>Nom du bucket *</Label>
                  <Input 
                    value={formData.config.bucket || ""}
                    onChange={(e) => updateConfig("bucket", e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label>Préfixe (dossier)</Label>
                <Input 
                  value={formData.config.prefix || ""}
                  onChange={(e) => updateConfig("prefix", e.target.value)}
                  placeholder="backups/"
                />
              </div>
            </>
          )}

          {formData.type === "linode" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Access Key *</Label>
                  <Input 
                    value={formData.config.access_key || ""}
                    onChange={(e) => updateConfig("access_key", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Secret Key *</Label>
                  <Input 
                    type="password"
                    value={formData.config.secret_key || ""}
                    onChange={(e) => updateConfig("secret_key", e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Cluster *</Label>
                  <Input 
                    value={formData.config.cluster || ""}
                    onChange={(e) => updateConfig("cluster", e.target.value)}
                    placeholder="eu-central-1"
                  />
                </div>
                <div>
                  <Label>Nom du bucket *</Label>
                  <Input 
                    value={formData.config.bucket || ""}
                    onChange={(e) => updateConfig("bucket", e.target.value)}
                  />
                </div>
              </div>
            </>
          )}

          <div className="flex items-center gap-2">
            <Switch 
              checked={formData.enabled}
              onCheckedChange={(v) => setFormData({...formData, enabled: v})}
            />
            <Label>Destination active</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Modal: Schedule
const ScheduleModal = ({ open, onClose, onSubmit, schedule, destinations }) => {
  const [formData, setFormData] = useState({
    name: "",
    frequency: "daily",
    day_of_week: 0,
    day_of_month: 1,
    hour: 2,
    minute: 0,
    backup_type: "full",
    destinations: [],
    compression: "gzip",
    compression_level: 6,
    retention_days: 30,
    enabled: true
  });

  useEffect(() => {
    if (schedule) {
      setFormData({
        name: schedule.name || "",
        frequency: schedule.frequency || "daily",
        day_of_week: schedule.day_of_week ?? 0,
        day_of_month: schedule.day_of_month ?? 1,
        hour: schedule.hour ?? 2,
        minute: schedule.minute ?? 0,
        backup_type: schedule.backup_type || "full",
        destinations: schedule.destinations || [],
        compression: schedule.compression || "gzip",
        compression_level: schedule.compression_level ?? 6,
        retention_days: schedule.retention_days ?? 30,
        enabled: schedule.enabled ?? true
      });
    } else {
      setFormData({
        name: "",
        frequency: "daily",
        day_of_week: 0,
        day_of_month: 1,
        hour: 2,
        minute: 0,
        backup_type: "full",
        destinations: [],
        compression: "gzip",
        compression_level: 6,
        retention_days: 30,
        enabled: true
      });
    }
  }, [schedule]);

  const handleSubmit = () => {
    if (!formData.name || formData.destinations.length === 0) return;
    onSubmit(formData);
  };

  const toggleDestination = (destId) => {
    setFormData(prev => ({
      ...prev,
      destinations: prev.destinations.includes(destId)
        ? prev.destinations.filter(d => d !== destId)
        : [...prev.destinations, destId]
    }));
  };

  const days = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{schedule ? "Modifier la planification" : "Nouvelle planification"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label>Nom de la planification *</Label>
            <Input 
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Ex: Backup quotidien"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Fréquence</Label>
              <Select 
                value={formData.frequency}
                onValueChange={(v) => setFormData({...formData, frequency: v})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Quotidien</SelectItem>
                  <SelectItem value="weekly">Hebdomadaire</SelectItem>
                  <SelectItem value="monthly">Mensuel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {formData.frequency === "weekly" && (
              <div>
                <Label>Jour</Label>
                <Select 
                  value={String(formData.day_of_week)}
                  onValueChange={(v) => setFormData({...formData, day_of_week: parseInt(v)})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {days.map((day, i) => (
                      <SelectItem key={i} value={String(i)}>{day}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {formData.frequency === "monthly" && (
              <div>
                <Label>Jour du mois</Label>
                <Select 
                  value={String(formData.day_of_month)}
                  onValueChange={(v) => setFormData({...formData, day_of_month: parseInt(v)})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({length: 28}, (_, i) => (
                      <SelectItem key={i+1} value={String(i+1)}>{i+1}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Heure</Label>
              <Select 
                value={String(formData.hour)}
                onValueChange={(v) => setFormData({...formData, hour: parseInt(v)})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({length: 24}, (_, i) => (
                    <SelectItem key={i} value={String(i)}>{String(i).padStart(2, "0")}:00</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type de backup</Label>
              <Select 
                value={formData.backup_type}
                onValueChange={(v) => setFormData({...formData, backup_type: v})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Complet</SelectItem>
                  <SelectItem value="data_only">Données seulement</SelectItem>
                  <SelectItem value="files_only">Fichiers seulement</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Destinations *</Label>
            <div className="space-y-2 mt-2">
              {destinations.filter(d => d.enabled).map(dest => (
                <div 
                  key={dest.id}
                  className={`p-2 border rounded cursor-pointer flex items-center gap-2 ${
                    formData.destinations.includes(dest.id) ? "border-blue-500 bg-blue-50" : ""
                  }`}
                  onClick={() => toggleDestination(dest.id)}
                >
                  <Checkbox checked={formData.destinations.includes(dest.id)} />
                  <DestinationIcon type={dest.type} />
                  <span className="text-sm">{dest.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Compression</Label>
              <Select 
                value={formData.compression}
                onValueChange={(v) => setFormData({...formData, compression: v})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gzip">GZIP</SelectItem>
                  <SelectItem value="zip">ZIP</SelectItem>
                  <SelectItem value="none">Aucune</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Rétention (jours)</Label>
              <Input 
                type="number"
                value={formData.retention_days}
                onChange={(e) => setFormData({...formData, retention_days: parseInt(e.target.value) || 30})}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch 
              checked={formData.enabled}
              onCheckedChange={(v) => setFormData({...formData, enabled: v})}
            />
            <Label>Planification active</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={!formData.name || formData.destinations.length === 0}>
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Modal: Restore
const RestoreModal = ({ open, onClose, onSubmit, backup, confirmKeyword, setConfirmKeyword }) => {
  const [formData, setFormData] = useState({
    restore_mode: "full",
    create_backup_before: true
  });

  const handleSubmit = () => {
    if (confirmKeyword !== "RESTAURER") return;
    onSubmit({
      backup_id: backup.id,
      source_destination: backup.destinations?.[0] || "",
      ...formData
    });
  };

  if (!backup) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertCircle className="w-5 h-5" />
            Restaurer une sauvegarde
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="font-medium text-amber-800">Attention - Action irréversible</p>
            <p className="text-sm text-amber-700 mt-1">
              Cette action va remplacer les données actuelles de l'application.
            </p>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm"><strong>Backup :</strong> {backup.comment || backup.id}</p>
            <p className="text-sm"><strong>Date :</strong> {formatDate(backup.created_at)}</p>
            <p className="text-sm"><strong>Taille :</strong> {formatSize(backup.size_bytes || 0)}</p>
          </div>

          <div>
            <Label>Mode de restauration</Label>
            <Select 
              value={formData.restore_mode}
              onValueChange={(v) => setFormData({...formData, restore_mode: v})}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="full">Restauration complète</SelectItem>
                <SelectItem value="data_only">Données seulement</SelectItem>
                <SelectItem value="files_only">Fichiers seulement</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox 
              checked={formData.create_backup_before}
              onCheckedChange={(v) => setFormData({...formData, create_backup_before: v})}
            />
            <Label>Créer un backup de sécurité avant la restauration</Label>
          </div>

          <Separator />

          <div>
            <Label>Pour confirmer, tapez <strong>RESTAURER</strong></Label>
            <Input 
              value={confirmKeyword}
              onChange={(e) => setConfirmKeyword(e.target.value.toUpperCase())}
              placeholder="RESTAURER"
              className="mt-2"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={handleSubmit}
            disabled={confirmKeyword !== "RESTAURER"}
            className="bg-amber-600 hover:bg-amber-700"
          >
            Restaurer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Modal: Import Backup
const ImportBackupModal = ({ open, onClose, onImport }) => {
  const [file, setFile] = useState(null);
  const [comment, setComment] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = React.useRef();

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.endsWith(".tar.gz") && !f.name.endsWith(".zip")) {
      alert("Seuls les fichiers .tar.gz et .zip sont acceptés");
      return;
    }
    setFile(f);
    if (!comment) setComment(`Import de ${f.name}`);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleSubmit = async () => {
    if (!file) return;
    setUploading(true);
    setUploadProgress(10);

    // Simulate progress during upload
    const interval = setInterval(() => {
      setUploadProgress(p => Math.min(p + 5, 85));
    }, 200);

    try {
      await onImport(file, comment);
      setUploadProgress(100);
    } finally {
      clearInterval(interval);
      setUploading(false);
      setUploadProgress(0);
      setFile(null);
      setComment("");
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return "";
    if (bytes > 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
    return `${(bytes / 1024).toFixed(0)} Ko`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-600" />
            Importer une sauvegarde
          </DialogTitle>
          <DialogDescription>
            Importez un fichier de sauvegarde (.tar.gz ou .zip) exporté depuis Baobab Connect.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Drop zone */}
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
              dragOver ? "border-blue-500 bg-blue-50" : file ? "border-emerald-400 bg-emerald-50" : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
            }`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            data-testid="import-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".tar.gz,.zip"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            {file ? (
              <div className="space-y-1">
                <FolderArchive className="w-10 h-10 text-emerald-500 mx-auto" />
                <p className="font-semibold text-emerald-700">{file.name}</p>
                <p className="text-sm text-emerald-600">{formatSize(file.size)}</p>
                <button
                  onClick={(e) => { e.stopPropagation(); setFile(null); setComment(""); }}
                  className="text-xs text-red-500 hover:text-red-700 underline mt-1"
                >
                  Changer de fichier
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="w-10 h-10 text-gray-300 mx-auto" />
                <p className="text-sm font-medium text-gray-600">
                  Glissez-déposez votre fichier ici
                </p>
                <p className="text-xs text-gray-400">ou cliquez pour sélectionner</p>
                <p className="text-xs text-gray-400 mt-1">Formats acceptés : .tar.gz, .zip</p>
              </div>
            )}
          </div>

          {/* Commentaire */}
          <div className="space-y-1">
            <Label className="text-sm font-medium">Commentaire (optionnel)</Label>
            <Input
              placeholder="Description de cette sauvegarde importée"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              data-testid="import-comment-input"
            />
          </div>

          {/* Infos */}
          <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-700 space-y-1">
            <p className="font-semibold">Informations :</p>
            <p>• Le fichier sera validé (structure et intégrité) avant import</p>
            <p>• Un checksum sera calculé pour éviter les doublons</p>
            <p>• Après import, vous pourrez lancer une restauration depuis ce fichier</p>
          </div>

          {/* Barre de progression */}
          {uploading && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-gray-500">
                <span>Import en cours...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-200"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={uploading}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!file || uploading}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="import-confirm-btn"
          >
            {uploading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Import en cours...</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" /> Importer</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function BackupManagementGuarded(props) {
  return (
    <RequirePermission
      anyPermission={[PERMS.AUDIT_LOGS_VIEW, PERMS.BACKUPS_CREATE, PERMS.BACKUPS_RESTORE, PERMS.BACKUPS_DOWNLOAD]}
    >
      <BackupManagement {...props} />
    </RequirePermission>
  );
}
